// src/components/ui/Card.js
export const Card = ({ children, style = {} }) => (
  <div style={{
    background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', ...style
  }}>
    {children}
  </div>
);

export const CardHeader = ({ title, subtitle, action, style = {} }) => (
  <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...style }}>
    <div>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{title}</h3>
      {subtitle && <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>{subtitle}</p>}
    </div>
    {action && <div>{action}</div>}
  </div>
);

export const StatCard = ({ label, value, subvalue, color = '#3b82f6', icon: Icon, trend }) => (
  <div style={{
    background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <p style={{ margin: '0 0 8px', fontSize: 12, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
        <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.5px' }}>{value}</p>
        {subvalue && <p style={{ margin: '4px 0 0', fontSize: 12, color: trend === 'up' ? '#10b981' : trend === 'down' ? '#ef4444' : '#64748b' }}>{subvalue}</p>}
      </div>
      {Icon && (
        <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={20} color={color} />
        </div>
      )}
    </div>
  </div>
);

export const Badge = ({ children, color = 'blue' }) => {
  const colors = {
    blue: { bg: '#eff6ff', text: '#1d4ed8' },
    green: { bg: '#f0fdf4', text: '#15803d' },
    red: { bg: '#fef2f2', text: '#dc2626' },
    orange: { bg: '#fff7ed', text: '#c2410c' },
    purple: { bg: '#faf5ff', text: '#7c3aed' },
    gray: { bg: '#f8fafc', text: '#475569' },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      background: c.bg, color: c.text, fontSize: 12, fontWeight: 600
    }}>
      {children}
    </span>
  );
};

export const Btn = ({ children, onClick, variant = 'primary', size = 'md', disabled, style = {}, type = 'button' }) => {
  const variants = {
    primary: { background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none' },
    success: { background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none' },
    danger: { background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', border: 'none' },
    ghost: { background: '#f8fafc', color: '#374151', border: '1px solid #e2e8f0' },
    outline: { background: 'transparent', color: '#3b82f6', border: '1px solid #3b82f6' },
  };
  const sizes = { sm: { padding: '6px 14px', fontSize: 13 }, md: { padding: '10px 20px', fontSize: 14 }, lg: { padding: '13px 28px', fontSize: 15 } };
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      ...variants[variant], ...sizes[size], borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer',
      fontWeight: 600, opacity: disabled ? 0.6 : 1, transition: 'opacity 0.15s', display: 'inline-flex',
      alignItems: 'center', gap: 6, ...style
    }}>
      {children}
    </button>
  );
};

export const Input = ({ label, error, style = {}, ...props }) => (
  <div style={style}>
    {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>{label}</label>}
    <input style={{
      width: '100%', padding: '9px 12px', border: `1px solid ${error ? '#ef4444' : '#d1d5db'}`,
      borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
      color: '#0f172a', background: '#fff', transition: 'border-color 0.15s'
    }} {...props} />
    {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#ef4444' }}>{error}</p>}
  </div>
);

export const Select = ({ label, error, children, style = {}, ...props }) => (
  <div style={style}>
    {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>{label}</label>}
    <select style={{
      width: '100%', padding: '9px 12px', border: `1px solid ${error ? '#ef4444' : '#d1d5db'}`,
      borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
      color: '#0f172a', background: '#fff', cursor: 'pointer'
    }} {...props}>
      {children}
    </select>
    {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#ef4444' }}>{error}</p>}
  </div>
);

export const Table = ({ headers, rows, emptyMessage = 'No data found' }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr style={{ background: '#f8fafc' }}>
          {headers.map((h, i) => (
            <th key={i} style={{
              padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600,
              color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px',
              borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap'
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={headers.length} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: 14 }}>{emptyMessage}</td></tr>
        ) : rows.map((row, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' }}
            onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
            onMouseLeave={e => e.currentTarget.style.background = ''}>
            {row.map((cell, j) => (
              <td key={j} style={{ padding: '12px 14px', color: '#0f172a' }}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const PageHeader = ({ title, subtitle, action }) => (
  <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
    <div>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.5px' }}>{title}</h1>
      {subtitle && <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const formatCurrency = (val) => {
  if (val == null) return '₹0.00';
  return '₹' + parseFloat(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatPct = (val) => {
  if (val == null) return '0.00%';
  const n = parseFloat(val);
  return (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
};
